import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import {
  updateAccreditationBody,
  destroyAccreditationBody,
} from '../../_data_sources/AccreditationBodies/manageAccreditationBodies';
import useAccreditationBody from '../../_data_sources/AccreditationBodies/useAccreditationBody';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { AccreditationBody } from '../../_types/awb';
import Loading from 'views/App/Portal/Loading';
import { Delete } from '@mui/icons-material';

export type EditAccreditingBodyProps = {
  id: string;
  setLastId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditId: React.Dispatch<React.SetStateAction<string | null>>;
};

const EditAccreditingBody: React.FC<EditAccreditingBodyProps> = ({
  id,
  setEditId,
  setLastId,
}: EditAccreditingBodyProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const accreditingBodyQuery = useAccreditationBody(id);

  const accreditingBodychema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodies.properties.name')} is required`),
    abbreviation: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('awb.accreditingBodies.properties.abbreviation')} is required`),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      abbreviation: '',
    },
    resolver: yupResolver(accreditingBodychema),
  });

  useEffect(() => {
    if (accreditingBodyQuery.data?.name) {
      reset(accreditingBodyQuery.data);
    }
  }, [accreditingBodyQuery.data, reset]);

  const handleSave: SubmitHandler<AccreditationBody> = (values: AccreditationBody) => {
    setIsSaving(true);
    updateAccreditationBody(
      id,
      values,
      (data) => {
        reset();
        setServerErrors(undefined);
        queryClient.invalidateQueries(['accrediting_bodies']);
        if (data.id) setLastId(data.id);
        setEditId(null);
        setIsSaving(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  const handleDelete = () => {
    confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('common.delete'),
      description: t('awb.accreditingBodies.delete.body'),
    })
      .then(async () => {
        setIsDeleting(true);
        await destroyAccreditationBody(
          id,
          () => {
            reset();
            queryClient.invalidateQueries(['accrediting_bodies']);
            setLastId(null);
            setEditId(null);
            enqueueSnackbar(parse(t(`awb.accreditingBodies.delete.success`)), {
              variant: 'success',
              autoHideDuration: 6000,
            });
            setIsDeleting(false);
          },
          (errors: ServerErrorResponse) => {
            setIsDeleting(false);
            setServerErrors(errors);
          }
        );
      })
      .catch(() => {
        setIsDeleting(false);
      });
  };

  const renderFormContent = () => {
    if (accreditingBodyQuery.isLoading) {
      return <Loading />;
    }

    if (isDeleting) {
      return (
        <Alert
          severity='warning'
          icon={<CircularProgress size={20} />}
        >
          {t('awb.accreditingBodies.edit.deleting')}
        </Alert>
      );
    }

    return (
      <>
        <Alert severity='warning'>{t('awb.accreditingBodies.edit.warning')}</Alert>
        {errorHelper.renderAllErrors(serverErrors, errors)}
        <TextField
          id='name'
          key='name'
          label={t('awb.accreditingBodies.properties.name')}
          variant='outlined'
          required
          fullWidth
          {...register('name')}
          error={!!errors.name}
          helperText={errors.name ? errors.name.message : undefined}
        />
        <TextField
          id='abbreviation'
          key='abbreviation'
          label={t('awb.accreditingBodies.properties.abbreviation')}
          variant='outlined'
          required
          fullWidth
          {...register('abbreviation')}
          error={!!errors.abbreviation}
          helperText={errors.abbreviation ? errors.abbreviation.message : undefined}
        />
      </>
    );
  };

  return (
    <Modal
      title={t('awb.accreditingBodies.edit.title')}
      onSubmit={handleSubmit(handleSave)}
      size='md'
      actions={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <Box>
            <Button
              className='icon-only'
              color='error'
              onClick={handleDelete}
            >
              <Delete />
            </Button>
          </Box>
          {!isDeleting && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                color='neutral'
                onClick={() => setEditId(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => trigger()}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('common.save')}
              </Button>
            </Box>
          )}
        </Box>
      }
      open={!!id}
      onClose={() => setEditId(null)}
    >
      {renderFormContent()}
    </Modal>
  );
};

export default EditAccreditingBody;
